import type { AnimState } from "../../art";
import { facingDirFromAim, facingDirHeld } from "../../art/facing-dir";
import type { CardinalDir } from "../../art/types";

export type WeaponShape =
  | "sword"
  | "spear"
  | "mace"
  | "cleaver"
  | "scythe"
  | "bow"
  | "greatsword"
  | "turret"
  | "rifle"
  | "wrench"
  | "claws"
  | "blade"
  | "staff";

/** Mesh ornament language — omitted/`default` keeps legacy pack looks. */
export type WeaponSkin = "default" | "holy" | "cursed";

/** Shield silhouette when `showShield` — tower = tall fortress plate. */
export type ShieldStyle = "heater" | "tower";

export type AttackStyle =
  | "slash"
  | "thrust"
  | "bash"
  | "poke"
  | "recoil"
  | "pulse";

export type RigRoleConfig = {
  weaponLength: number;
  gripOffset: number;
  bladeColor: number;
  bobAmp: number;
  bobHz: number;
  weaponBobMul: number;
  /** View-only aim turn rate (rad/s); slower than sim aimTurnRate. */
  displayAimTurnRate: number;
  /** Keep prior foot side while |cos(aim)| < this (vertical tremble). */
  footSideHoldCos: number;
  trailEnabled: boolean;
  /** Multiplier on displaySize/40 dust scale (1 = party). */
  moveDustScaleMul: number;
  /** Cap for dust scale (party 2.5; large bodies raise via tables). */
  moveDustScaleMax: number;
  /** Dust puff fill alpha. */
  moveDustAlpha: number;
  /** Min seconds between dust puffs on this rig. */
  moveDustCdSec: number;
  /** Lateral spawn offset multiplier behind facing (1 = party). */
  moveDustSpread: number;
  readyOffset: number;
  shadowRx: number;
  shadowRy: number;
  /**
   * Local Y of ground shadow / dust at `refDisplay` (positive = toward feet).
   * When set, scales with displaySize/refDisplay; else `footBiasFrac` is used.
   */
  shadowOffsetY: number | null;
  /** Fraction of half-display used as foot bias when `shadowOffsetY` is null. */
  footBiasFrac: number;
  /** Soft oval fill alpha (0–1). */
  shadowAlpha: number;
  hideBody: boolean;
  showFeet: boolean;
  showHand: boolean;
  /** When false, body owns combat silhouette (baked fists/barrel/horns); UnitRig weapon mesh is hidden. */
  showWeapon: boolean;
  weaponShape: WeaponShape;
  /** Faction ornament; default = legacy silhouette for other packs. */
  weaponSkin: WeaponSkin;
  /** Heater (default) or tall tower plate when showShield. */
  shieldStyle: ShieldStyle;
  attackStyle: AttackStyle;
  swingAmp: number;
  swingSec: number;
  glow: boolean;
  showShield: boolean;
};

type RigDefaults = {
  weaponLength: number;
  gripOffset: number;
  bladeColor: string;
  bobAmp: number;
  bobHz?: number;
  weaponBobMul?: number;
  displayAimTurnRate?: number;
  footSideHoldCos?: number;
  trailEnabled: boolean;
  moveDustScaleMul?: number;
  moveDustScaleMax?: number;
  moveDustAlpha?: number;
  moveDustCdSec?: number;
  moveDustSpread?: number;
  readyOffset: number;
  shadowRx: number;
  shadowRy: number;
  shadowOffsetY?: number | null;
  footBiasFrac?: number;
  shadowAlpha?: number;
  hideBody: boolean;
  showFeet: boolean;
  showHand: boolean;
  showWeapon?: boolean;
  weaponShape?: WeaponShape;
  weaponSkin?: WeaponSkin;
  shieldStyle?: ShieldStyle;
  attackStyle?: AttackStyle;
  swingAmp?: number;
  swingSec?: number;
  glow?: boolean;
  showShield?: boolean;
};

export type RigTables = {
  trailBudgetPerFrame: number;
  defaults: RigDefaults;
  byRole: Record<string, Partial<RigDefaults>>;
};

const SHAPES: WeaponShape[] = [
  "sword",
  "spear",
  "mace",
  "cleaver",
  "scythe",
  "bow",
  "greatsword",
  "turret",
  "rifle",
  "wrench",
  "claws",
  "blade",
  "staff",
];

const SKINS: WeaponSkin[] = ["default", "holy", "cursed"];
const SHIELD_STYLES: ShieldStyle[] = ["heater", "tower"];

const STYLES: AttackStyle[] = [
  "slash",
  "thrust",
  "bash",
  "poke",
  "recoil",
  "pulse",
];

/** Min seconds between move-dust puffs on one rig (light frequency). */
export const MOVE_DUST_TRAIL_CD_SEC = 0.16;

function parseHex(raw: string | number | undefined, fallback: number): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw !== "string") return fallback;
  const cleaned = raw.replace(/^0x/i, "");
  const n = Number.parseInt(cleaned, 16);
  return Number.isFinite(n) ? n : fallback;
}

function asShape(v: unknown, fallback: WeaponShape): WeaponShape {
  return typeof v === "string" && (SHAPES as string[]).includes(v)
    ? (v as WeaponShape)
    : fallback;
}

function asSkin(v: unknown, fallback: WeaponSkin): WeaponSkin {
  return typeof v === "string" && (SKINS as string[]).includes(v)
    ? (v as WeaponSkin)
    : fallback;
}

function asShieldStyle(v: unknown, fallback: ShieldStyle): ShieldStyle {
  return typeof v === "string" && (SHIELD_STYLES as string[]).includes(v)
    ? (v as ShieldStyle)
    : fallback;
}

function asStyle(v: unknown, fallback: AttackStyle): AttackStyle {
  return typeof v === "string" && (STYLES as string[]).includes(v)
    ? (v as AttackStyle)
    : fallback;
}

/** Resolve per-role rig knobs from presentation tables. */
export function resolveRigConfig(
  tables: RigTables | undefined,
  role: string,
): RigRoleConfig {
  const d = tables?.defaults;
  const o = tables?.byRole?.[role] ?? {};
  return {
    weaponLength: Number(o.weaponLength ?? d?.weaponLength) || 20,
    gripOffset: Number(o.gripOffset ?? d?.gripOffset) || 5,
    bladeColor: parseHex(
      (o.bladeColor ?? d?.bladeColor) as string | undefined,
      0x6ee7ff,
    ),
    bobAmp: (() => {
      const v = Number(o.bobAmp ?? d?.bobAmp);
      return Number.isFinite(v) ? v : 0.55;
    })(),
    bobHz: Number(o.bobHz ?? d?.bobHz) || 2,
    weaponBobMul: Number(o.weaponBobMul ?? d?.weaponBobMul ?? 0.05),
    displayAimTurnRate:
      Number(o.displayAimTurnRate ?? d?.displayAimTurnRate) || 2.2,
    footSideHoldCos: Number(o.footSideHoldCos ?? d?.footSideHoldCos ?? 0.22),
    trailEnabled: (o.trailEnabled ?? d?.trailEnabled) !== false,
    moveDustScaleMul: (() => {
      const v = Number(o.moveDustScaleMul ?? d?.moveDustScaleMul ?? 1);
      return Number.isFinite(v) ? Math.max(0.25, Math.min(4, v)) : 1;
    })(),
    moveDustScaleMax: (() => {
      const v = Number(o.moveDustScaleMax ?? d?.moveDustScaleMax ?? 2.5);
      return Number.isFinite(v) ? Math.max(0.5, Math.min(6, v)) : 2.5;
    })(),
    moveDustAlpha: (() => {
      const v = Number(o.moveDustAlpha ?? d?.moveDustAlpha ?? 0.4);
      return Number.isFinite(v) ? Math.max(0.05, Math.min(0.8, v)) : 0.4;
    })(),
    moveDustCdSec: (() => {
      const v = Number(
        o.moveDustCdSec ?? d?.moveDustCdSec ?? MOVE_DUST_TRAIL_CD_SEC,
      );
      return Number.isFinite(v)
        ? Math.max(0.05, Math.min(1.5, v))
        : MOVE_DUST_TRAIL_CD_SEC;
    })(),
    moveDustSpread: (() => {
      const v = Number(o.moveDustSpread ?? d?.moveDustSpread ?? 1);
      return Number.isFinite(v) ? Math.max(0.5, Math.min(3, v)) : 1;
    })(),
    readyOffset: Number(o.readyOffset ?? d?.readyOffset ?? -0.55),
    shadowRx: Number(o.shadowRx ?? d?.shadowRx) || 10,
    shadowRy: Number(o.shadowRy ?? d?.shadowRy) || 4,
    shadowOffsetY: (() => {
      const raw = o.shadowOffsetY ?? d?.shadowOffsetY;
      if (raw == null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })(),
    footBiasFrac: (() => {
      const v = Number(o.footBiasFrac ?? d?.footBiasFrac ?? 0.28);
      return Number.isFinite(v) ? Math.max(0, v) : 0.28;
    })(),
    shadowAlpha: (() => {
      const v = Number(o.shadowAlpha ?? d?.shadowAlpha ?? 0.26);
      if (!Number.isFinite(v)) return 0.26;
      return Math.max(0.05, Math.min(0.6, v));
    })(),
    hideBody: Boolean(o.hideBody ?? d?.hideBody),
    showFeet: (o.showFeet ?? d?.showFeet) !== false,
    showHand: (o.showHand ?? d?.showHand) !== false,
    showWeapon: (o.showWeapon ?? d?.showWeapon) !== false,
    weaponShape: asShape(o.weaponShape ?? d?.weaponShape, "sword"),
    weaponSkin: asSkin(o.weaponSkin ?? d?.weaponSkin, "default"),
    shieldStyle: asShieldStyle(o.shieldStyle ?? d?.shieldStyle, "heater"),
    attackStyle: asStyle(o.attackStyle ?? d?.attackStyle, "slash"),
    swingAmp: Number(o.swingAmp ?? d?.swingAmp) || 1,
    swingSec: Number(o.swingSec ?? d?.swingSec) || 0.28,
    glow: (o.glow ?? d?.glow) !== false,
    showShield: Boolean(o.showShield ?? d?.showShield),
  };
}

export type AttackMotion = { rot: number; extend: number };

/**
 * Role-specific attack motion. rot = radians added to aim; extend = px along blade.
 */
export function attackMotion(
  animState: AnimState,
  animTime: number,
  style: AttackStyle,
  amp: number,
  sec: number,
): AttackMotion {
  if (animState !== "attack") return { rot: 0, extend: 0 };
  const dur = Math.max(0.08, sec);
  const t = Math.max(0, Math.min(1, animTime / dur));
  const a = Math.max(0.2, amp);

  switch (style) {
    case "thrust": {
      // Minimal rotate, strong forward poke.
      const e =
        t < 0.4 ? (t / 0.4) * 10 * a : 10 * a * (1 - (t - 0.4) / 0.6);
      return { rot: -0.08 * a * (1 - t), extend: e };
    }
    case "bash": {
      // Shield/mace bash: wind-up pull-back → strong forward thrust → recover.
      // Extend-led (not rot-led) so UnitRig shield reads as a strike, not a blink.
      if (t < 0.28)
        return {
          rot: -0.4 * a * (t / 0.28),
          extend: -5 * a * (t / 0.28),
        };
      if (t < 0.55) {
        const u = (t - 0.28) / 0.27;
        return {
          rot: -0.4 * a + 0.75 * a * u,
          extend: -5 * a + 16 * a * u,
        };
      }
      const u = (t - 0.55) / 0.45;
      return {
        rot: 0.35 * a * (1 - u),
        extend: 11 * a * (1 - u),
      };
    }
    case "poke": {
      // Bow draw then release (slight back then forward).
      if (t < 0.45) return { rot: -0.12 * a, extend: -4 * a * (t / 0.45) };
      const u = (t - 0.45) / 0.55;
      return { rot: -0.05 * a * (1 - u), extend: 8 * a * Math.sin(u * Math.PI) };
    }
    case "pulse": {
      // Staff cast: soft bob + short thrust (no sword arc).
      const e = 5 * a * Math.sin(t * Math.PI);
      return { rot: -0.1 * a * Math.sin(t * Math.PI * 2), extend: e };
    }
    case "recoil": {
      if (t < 0.25) return { rot: 0.05 * a, extend: -6 * a * (t / 0.25) };
      return {
        rot: 0.02 * a,
        extend: -6 * a * (1 - (t - 0.25) / 0.75),
      };
    }
    case "slash":
    default: {
      // Pole-sweep: wind-up → wide arc → recover (readable at board scale).
      if (t < 0.3) return { rot: -1.05 * a * (t / 0.3), extend: 1 * a * (t / 0.3) };
      if (t < 0.65)
        return {
          rot: -1.05 * a + 2.35 * a * ((t - 0.3) / 0.35),
          extend: 3.5 * a,
        };
      return {
        rot: 1.3 * a * (1 - (t - 0.65) / 0.35),
        extend: 3.5 * a * (1 - (t - 0.65) / 0.35),
      };
    }
  }
}

/** @deprecated prefer attackMotion — kept for call-site clarity on rot-only. */
export function weaponSwingOffset(
  animState: AnimState,
  animTime: number,
  style: AttackStyle = "slash",
  amp = 1,
  sec = 0.28,
): number {
  return attackMotion(animState, animTime, style, amp, sec).rot;
}

export function bodyBobY(
  animState: AnimState,
  animTime: number,
  amp: number,
  hz = 2,
): number {
  if (animState !== "walk" || amp <= 0) return 0;
  const f = Math.max(0.5, hz);
  return Math.sin(animTime * Math.PI * 2 * f) * amp;
}

/**
 * View-side aim step: shortest-arc wrap + per-frame max rotation.
 * Tiny deltas are ignored (deadzone) to kill micro-jitter on long marches.
 */
export function stepDisplayAim(
  current: number,
  target: number,
  maxRadPerSec: number,
  dt: number,
  deadzoneRad = 0.035,
): number {
  let delta = target - current;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  if (Math.abs(delta) < Math.max(0, deadzoneRad)) return current;
  const maxStep = Math.max(0.05, maxRadPerSec) * Math.max(0, dt);
  if (Math.abs(delta) <= maxStep) return target;
  return current + Math.sign(delta) * maxStep;
}

/** Per-unit display facing memory for ArtPipeline body frames. */
export type DisplayFacingState = {
  aim: number | null;
  facing: CardinalDir | null;
  pendingFacing: CardinalDir | null;
  pendingFrames: number;
};

export function createDisplayFacingState(): DisplayFacingState {
  return {
    aim: null,
    facing: null,
    pendingFacing: null,
    pendingFrames: 0,
  };
}

export type StepDisplayFacingOpts = {
  aimDeadzoneRad?: number;
  /** Extra radians past ±45° before cardinal flips (sector hysteresis). */
  holdRad?: number;
  /** Consecutive frames the held candidate must stick before committing. */
  dwellFrames?: number;
};

/**
 * Rate-limit display aim, then derive cardinal facing with hysteresis + dwell.
 * Use the returned `facing` for `ensureAnimFrame` / body textures (not raw aim).
 */
export function stepDisplayFacing(
  state: DisplayFacingState,
  targetAim: number,
  maxRadPerSec: number,
  dt: number,
  opts: StepDisplayFacingOpts = {},
): { aim: number; facing: CardinalDir; state: DisplayFacingState } {
  const aimDeadzone = opts.aimDeadzoneRad ?? 0.035;
  const holdRad = opts.holdRad ?? 0.28;
  const dwellFrames = Math.max(0, Math.floor(opts.dwellFrames ?? 2));

  const aim =
    state.aim == null
      ? targetAim
      : stepDisplayAim(state.aim, targetAim, maxRadPerSec, dt, aimDeadzone);

  const prevFacing = state.facing ?? facingDirFromAim(aim);
  const candidate = facingDirHeld(prevFacing, aim, holdRad);

  let facing = prevFacing;
  let pendingFacing = state.pendingFacing;
  let pendingFrames = state.pendingFrames;

  if (candidate === prevFacing) {
    pendingFacing = null;
    pendingFrames = 0;
  } else if (dwellFrames <= 0) {
    facing = candidate;
    pendingFacing = null;
    pendingFrames = 0;
  } else if (pendingFacing === candidate) {
    pendingFrames += 1;
    if (pendingFrames >= dwellFrames) {
      facing = candidate;
      pendingFacing = null;
      pendingFrames = 0;
    }
  } else {
    pendingFacing = candidate;
    pendingFrames = 1;
    if (pendingFrames >= dwellFrames) {
      facing = candidate;
      pendingFacing = null;
      pendingFrames = 0;
    }
  }

  const next: DisplayFacingState = {
    aim,
    facing,
    pendingFacing,
    pendingFrames,
  };
  return { aim, facing, state: next };
}

/**
 * Foot L/R side from aim, with hysteresis near vertical so cos(aim)~0
 * does not flip feet every frame.
 */
export function footSideFromAim(
  aim: number,
  prevSide: number,
  holdCosEps: number,
): number {
  const c = Math.cos(aim);
  const eps = Math.max(0, holdCosEps);
  if (Math.abs(c) < eps) {
    return prevSide >= 0 ? 1 : -1;
  }
  return c >= 0 ? 1 : -1;
}

/**
 * When facing north (away / up), draw weapon under the body sprite so the
 * character occludes the blade. Other facings keep weapon in front.
 */
export function weaponDrawnBehindBody(facingDir: CardinalDir): boolean {
  return facingDir === "n";
}

export function footPhaseOffsets(
  animState: AnimState,
  animTime: number,
  hz = 2,
): { left: number; right: number } {
  if (animState !== "walk") return { left: 0, right: 0 };
  const f = Math.max(0.5, hz);
  const phase = animTime * Math.PI * 2 * f;
  return { left: Math.sin(phase) * 2.2, right: Math.sin(phase + Math.PI) * 2.2 };
}

/** Grip local position along aim (before weapon container rotation). */
export function gripLocalPos(
  gripOffset: number,
  readyOffset: number,
): { x: number; y: number } {
  return {
    x: gripOffset * Math.cos(readyOffset),
    y: gripOffset * Math.sin(readyOffset) - 2,
  };
}

/**
 * Scale procedural weapon/shield/hand to match body display growth
 * (e.g. Frontline tier display). Authored `weaponLength` stays at ref size.
 */
export function propScaleFromBodyDisplay(
  displaySize: number,
  refDisplaySize: number,
): number {
  const ref = Math.max(1, refDisplaySize);
  const raw = Math.max(8, displaySize) / ref;
  return Math.max(0.25, Math.min(4, raw));
}

/**
 * Local Y bias from container origin down toward feet/root.
 * Prefers authored `shadowOffsetY` (at `refDisplay`, then scaled); else
 * `footBiasFrac * displaySize/2` (party default ~0.28).
 */
export type FootBiasOpts = {
  shadowOffsetY?: number | null;
  footBiasFrac?: number;
  refDisplay?: number;
};

export function footBiasFromDisplay(
  displaySize: number,
  opts: FootBiasOpts = {},
): number {
  const size = Math.max(8, displaySize);
  const ref = Math.max(8, opts.refDisplay ?? 40);
  const authored = opts.shadowOffsetY;
  if (authored != null && Number.isFinite(authored)) {
    return authored * (size / ref);
  }
  const frac =
    opts.footBiasFrac != null && Number.isFinite(opts.footBiasFrac)
      ? Math.max(0, opts.footBiasFrac)
      : 0.28;
  return frac * (size * 0.5);
}

/**
 * Soft ground shadow ellipse from authored rx/ry, scaled by body display size.
 * `refDisplay` ≈ typical unit chip (~40px); boss/large bodies grow the oval.
 * `y` is foot/root bias (not body center).
 */
export function shadowEllipseFromDisplay(
  displaySize: number,
  shadowRx: number,
  shadowRy: number,
  opts: FootBiasOpts = {},
): { rx: number; ry: number; y: number } {
  const ref = Math.max(8, opts.refDisplay ?? 40);
  const scale = Math.max(0.5, Math.max(8, displaySize) / ref);
  const rx = Math.max(4, shadowRx * scale);
  const ry = Math.max(2, shadowRy * scale);
  const y = footBiasFromDisplay(displaySize, { ...opts, refDisplay: ref });
  return { rx, ry, y };
}

/**
 * Local tip / muzzle along the weapon axis from grip.
 * `tipFrac` default 0.85 ≈ blade tip / barrel mouth (not past the mesh).
 */
export function muzzleLocal(
  gx: number,
  gy: number,
  rot: number,
  weaponLength: number,
  tipFrac = 0.85,
): { x: number; y: number } {
  const len = Math.max(4, weaponLength) * tipFrac;
  return {
    x: gx + Math.cos(rot) * len,
    y: gy + Math.sin(rot) * len,
  };
}

export function trailSpawnChance(
  intentSpeed: number,
  walkEnter: number,
): boolean {
  return intentSpeed >= walkEnter * 0.55;
}

/**
 * Foot-dust scale from body display size.
 * Party (~40–46px) stays near 1; large bodies grow until `scaleMax`.
 */
export function moveDustScaleFromDisplay(
  displaySize: number,
  opts: { scaleMul?: number; scaleMax?: number } = {},
): number {
  const mul =
    opts.scaleMul != null && Number.isFinite(opts.scaleMul)
      ? Math.max(0.25, opts.scaleMul)
      : 1;
  const max =
    opts.scaleMax != null && Number.isFinite(opts.scaleMax)
      ? Math.max(0.5, opts.scaleMax)
      : 2.5;
  const auto = Math.max(8, displaySize) / 40;
  return Math.max(0.5, Math.min(max, auto * mul));
}
